import { getLanguagesList } from './utils/localization';

/**
 * Contract: CSV of exact paths or paths ending with "/*" (other * are ignored). Examples: "blog/*, /company/contact" -> matches "/blog" and "/blog/x"; "/blogs" does not.
 */

// trim, drop trailing slashes, ensure leading "/", strip configured language prefix.
const stripTrailingSlashes = (path: string) => {
  let end = path.length;
  while (end > 0 && path[end - 1] === '/') {
    end -= 1;
  }
  return path.slice(0, end);
};

const normalizePath = (path: string): string => {
  const trimmedPath = path.trim();
  if (!trimmedPath) {
    return '';
  }

  const withoutTrailingSlash = stripTrailingSlashes(trimmedPath);
  const withLeadingSlash = withoutTrailingSlash.startsWith('/')
    ? withoutTrailingSlash
    : '/' + withoutTrailingSlash;

  return withLeadingSlash;
};

const stripConfiguredLanguagePrefix = (path: string): string => {
  const languages = getLanguagesList().filter((language) => language !== 'en');
  const matchingLanguage = languages.find(
    (language) => path === `/${language}` || path.startsWith(`/${language}/`)
  );

  if (!matchingLanguage) {
    return path;
  }

  return path.slice(matchingLanguage.length + 1) || '/';
};

const matchesMigrationPath = (pattern: string, path: string): boolean => {
  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -2);
    return path === prefix || path.startsWith(`${prefix}/`);
  }

  return path === pattern;
};

export const parseMigratedPaths = (flag?: string | null): string[] => (flag ?? '').split(',');

const getGatsbyFlag = (): string | null | undefined => {
  const gatsbyFlag = process.env.GATSBY_ENABLE_MIGRATION_PATHS;
  const nodeEnv = process.env.NODE_ENV;
  return nodeEnv === 'production' ? gatsbyFlag : '';
};

export const hasActiveRR7Migration = parseMigratedPaths(getGatsbyFlag()).some(
  (p) => p.trim().length > 0
);

export const isPathMigratedToRR7 = (path: string) => {
  const normalizedPath = stripConfiguredLanguagePrefix(normalizePath(path) || '/');
  const enableMigrationPaths = getGatsbyFlag();
  return parseMigratedPaths(enableMigrationPaths).some((pattern) =>
    matchesMigrationPath(normalizePath(pattern), normalizedPath)
  );
};

/**
 * Returns normalized base paths (without trailing `/*`) for all wildcard migration patterns.
 * e.g. `"blog/*, /company/contact"` → `["/blog"]`
 */
const WILDCARD_SUFFIX = '/*';

export const getWildcardMigrationBasePaths = (): string[] =>
  parseMigratedPaths(getGatsbyFlag())
    .filter((p) => p.trim().endsWith(WILDCARD_SUFFIX))
    .map((p) => normalizePath(p.trim().slice(0, -WILDCARD_SUFFIX.length)))
    .filter(Boolean);

/**
 * Returns true if the given path falls under a wildcard migration pattern,
 * meaning per-page redirects should be skipped in favour of a single wildcard rule.
 */
export const isPathCoveredByWildcardMigration = (path: string): boolean => {
  const normalizedPath = normalizePath(path) || '/';
  return getWildcardMigrationBasePaths().some(
    (base) => normalizedPath === base || normalizedPath.startsWith(`${base}/`)
  );
};
